<script lang="ts" setup>
import { costs } from '@thecompaniesapi/shared/client'
import { getRootDomainAndFull, isValidFullDomain, isValidUrl } from '@thecompaniesapi/url-helpers'
import Papa from 'papaparse'

const emit = defineEmits<{
  close: []
  initialized: []
}>()

const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const { t } = useI18n()
const api = useApi()
const actionsStore = useActionsStore()
const listsStore = useListsStore()
const localePath = useLocalePath()
const router = useRouter()
const teamsStore = useTeamsStore()
const enrichmentStore = useEnrichmentStore()
const { toggleModal } = useOverlays()

const companiesImported = ref(0)
const companiesInPreview = ref(10)
const companiesPerBatch = 250
const domainFound = ref(false)
const fetchingExistingDomains = ref(false)
const importing = ref(false)
const importingError = ref(false)
const initializing = ref(false)

const form = reactive({
  columnsMatcher: {
    columns: {} as FieldColumnsMatcherColumns,
    columnsMatched: {} as FieldColumnsMatcherModelValue,
    companies: [] as { [key: string]: string }[],
    companiesCount: 0,
  },
  countrySelector: { country: 'all' },
  domains: {
    existing: [] as string[],
    missing: [] as string[],
  },
  listSelector: {
    listId: undefined as number | undefined,
    listName: '',
  },
})

const hasNameMatchedColumn = computed(() => {
  return Object.values(form.columnsMatcher.columnsMatched).includes('name')
})

const canImport = computed(() => {
  if (!Object.values(form?.columnsMatcher?.columnsMatched || {}).filter(columnValue => columnValue !== 'choose' && !!columnValue).length) {
    return t('mustMatchColumns')
  }

  if (!form.listSelector?.listId) {
    return t('mustSelectList')
  }

  return true
})

const enricheableCompaniesCount = computed(() => {
  const availableCredits = teamsStore.currentTeam?.credits || 0
  const existingCount = form.columnsMatcher.companiesCount - form.domains.missing.length
  const missingCount = form.domains.missing.length

  // First, enrich existing companies (costs.globals.company credit each)
  const enrichableExistingCount = Math.min(existingCount, Math.floor(availableCredits / costs.globals.company))
  const creditsAfterExisting = availableCredits - (enrichableExistingCount * costs.globals.company)

  // Then, enrich missing companies (costs.actions['enrich-companies'] credits each)
  const enrichableMissingCount = Math.min(missingCount, Math.floor(creditsAfterExisting / costs.actions['enrich-companies']))

  // Cost
  const totalCost = (costs.actions['enrich-companies'] * missingCount) + (costs.globals.company * existingCount)
  const effectiveCost = (costs.actions['enrich-companies'] * enrichableMissingCount) + (costs.globals.company * enrichableExistingCount)
  const creditsAfter = totalCost > (teamsStore.currentTeam?.credits || 0)
    ? 0
    : (teamsStore.currentTeam?.credits || 0) - totalCost
  const missingCredits = totalCost > (teamsStore.currentTeam?.credits || 0)
    ? totalCost - (teamsStore.currentTeam?.credits || 0)
    : 0

  const result = {
    creditsAfter,
    effectiveCost,
    enrichedCount: enrichableExistingCount + enrichableMissingCount,
    existingCompaniesCount: existingCount,
    missingCompaniesCount: missingCount,
    missingCredits,
    remainingCredits: availableCredits,
    remainingExistingCompaniesCount: existingCount - enrichableExistingCount,
    remainingMissingCompaniesCount: missingCount - enrichableMissingCount,
    remainingTotal: existingCount + missingCount - (enrichableExistingCount + enrichableMissingCount),
    totalCost,
  }

  return result
})

watch(
  () => form.columnsMatcher.columnsMatched,
  async () => await getExistingDomains(),
)

async function onClose() {
  emit('close')

  enrichmentStore.reset()

  toggleModal({
    opened: false,
    type: 'listEnrichment',
  })
}

async function countCompaniesAndGenerateColumns() {
  initializing.value = true

  return new Promise((resolve, reject) => {
    if (!enrichmentStore.fileToImport) {
      return true
    }

    Papa.parse<{ [key: string]: string }>(enrichmentStore.fileToImport, {
      comments: '#',
      header: true,
      skipEmptyLines: 'greedy',
      worker: true,

      complete: async () => {
        initializing.value = true

        emit('initialized')
        resolve(true)
      },

      error: (error) => {
        initializing.value = false
        importingError.value = true

        reject(error)
      },

      step: (row) => {
        form.columnsMatcher.companiesCount += 1

        if (form.columnsMatcher.companiesCount >= enrichmentStore.maxCompanies) {
          return
        }

        // Sample the first 100 rows to generate columns and preview data
        if (form.columnsMatcher.companiesCount < 100) {
          // Generate columns and build preview data for each column
          for (const [key, value] of Object.entries(row.data)) {
            if (value) {
              if (!form.columnsMatcher.columns[key]) {
                form.columnsMatcher.columns[key] = [value]
              }
              else if (!form.columnsMatcher.columns[key].includes(value) && form.columnsMatcher.columns[key].length < companiesInPreview.value) {
                form.columnsMatcher.columns[key].push(value)
              }
            }
          }

          // Automatically match columns to their corresponding social media or domain type
          // based on analyzing the preview values
          const socialNetworks = {
            facebook: ['//facebook.com/', '//www.facebook.com/'],
            github: ['//github.com/', '//www.github.com/'],
            instagram: ['//instagram.com/', '//www.instagram.com/'],
            linkedin: ['linkedin.com/company/', 'linkedin.com/school/'],
            pinterest: ['pinterest.'],
            snapchat: ['//snapchat.com/add/', '//www.snapchat.com/add/'],
            tiktok: ['//tiktok.com/@', '//www.tiktok.com/@'],
            twitter: [
              '//twitter.com/',
              '//www.twitter.com/',
              '//x.com/',
              '//www.x.com/',
            ],
            youtube: ['//youtube.com/', '//www.youtube.com/'],
          }

          for (const [columnName, previewValues] of Object.entries(form.columnsMatcher.columns)) {
            columnDetection: for (const previewValue of previewValues) {
              for (const [socialNetwork, socialNetworkValues] of Object.entries(socialNetworks)) {
                for (const socialNetworkValue of socialNetworkValues) {
                  if (previewValue.includes(socialNetworkValue)) {
                    form.columnsMatcher.columnsMatched[columnName] = socialNetwork
                    break columnDetection
                  }
                }
              }

              if ((isValidUrl(previewValue) || isValidFullDomain(previewValue)) && !domainFound.value) {
                form.columnsMatcher.columnsMatched[columnName] = 'domain'
                domainFound.value = true
                break columnDetection
              }
            }
          }
        }
      },
    })
  })
}

function importCompaniesFromFile(params: {
  country: string
  listId: number
}) {
  let isFirstPage = true

  return new Promise((resolve, reject) => {
    Papa.parse<{ [key: string]: string }>(enrichmentStore.fileToImport!, {
      comments: '#',
      header: true,
      skipEmptyLines: 'greedy',
      worker: true,

      complete: async () => {
        if (!importingError.value) {
          const companies = form.columnsMatcher.companies.length ? [...form.columnsMatcher.companies] : []

          // Save last batch to server
          await saveBatchOfCompanies({
            companies,
            country: params.country,
            isFirstPage: false,
            isLastPage: true,
            listId: params.listId,
          })
        }

        return resolve(true)
      },

      error: (error) => {
        importing.value = false
        importingError.value = true

        return reject(error)
      },

      step: async (row) => {
        form.columnsMatcher.companies.push(row.data)

        // Save companies to server
        if (form.columnsMatcher.companies.length === companiesPerBatch) {
          const companies = [...form.columnsMatcher.companies]

          form.columnsMatcher.companies = []

          // Initialize payload
          const payload = {
            companies,
            country: params.country,
            isFirstPage: false,
            isLastPage: false,
            listId: params.listId,
          }

          // Detect the first page
          if (isFirstPage) {
            isFirstPage = false
            payload.isFirstPage = true
          }

          await saveBatchOfCompanies(payload)
        }
      },
    })
  })
}

async function getExistingDomains() {
  if (!domainFound.value) {
    return
  }

  const domainColumn = Object.keys(form.columnsMatcher.columnsMatched).find(key => form.columnsMatcher.columnsMatched[key] === 'domain')

  if (!domainColumn) {
    return
  }

  const domains = await new Promise<string[]>((resolve) => {
    const foundDomains: string[] = []

    let rowsCount = 0

    const dedupeAndFormatDomains = () => Array.from(new Set(foundDomains.map(domain => getRootDomainAndFull(domain).full))).filter(Boolean)

    Papa.parse<{ [key: string]: string }>(enrichmentStore.fileToImport!, {
      comments: '#',
      header: true,
      skipEmptyLines: 'greedy',
      worker: true,

      complete: () => resolve(dedupeAndFormatDomains()),
      error: () => resolve(dedupeAndFormatDomains()),
      step: (row) => {
        rowsCount += 1

        if (rowsCount >= enrichmentStore.maxCompanies) {
          return
        }

        foundDomains.push(row.data[domainColumn])
      },
    })
  })

  fetchingExistingDomains.value = true

  try {
    const existingResponse = await api.companies.fetchExisting(domains)

    form.domains.existing = existingResponse.data.existing
    form.domains.missing = existingResponse.data.missing
  }
  catch {}

  fetchingExistingDomains.value = false
}

async function saveBatchOfCompanies(params: {
  companies: { [key: string]: string }[]
  country: string
  isFirstPage: boolean
  isLastPage: boolean
  listId: number
}) {
  // Clean columns matched
  const columnsMatched = { ...form.columnsMatcher.columnsMatched }

  // Remove useless keys from columnsMatched
  for (const key in columnsMatched) {
    if (columnsMatched[key] === 'choose') {
      delete columnsMatched[key]
    }
  }

  // Remove useless keys from companies
  for (const company of params.companies) {
    for (const companyKey in company) {
      if (!columnsMatched[companyKey]) {
        delete company[companyKey]
      }
    }
  }

  await listsStore.toggleCompaniesFromList({
    columnsMatched,
    companies: params.companies as any,
    country: params.country,
    isFirstPage: params.isFirstPage || false,
    isLastPage: params.isLastPage || false,
    listId: params.listId,
  })

  companiesImported.value += params.companies.length
}

async function setForm() {
  await countCompaniesAndGenerateColumns()

  // Add file name to list name
  if (enrichmentStore.fileToImport && enrichmentStore.fileToImport.name) {
    form.listSelector.listName = enrichmentStore.fileToImport.name.split('.')[0]
  }
}

async function onImportCompanies() {
  document.querySelector('.js-slideover-body')?.scrollTo(0, 0)

  importing.value = true

  try {
    await importCompaniesFromFile({
      country: form.countrySelector.country,
      listId: form.listSelector.listId!,
    })

    if (!importingError.value) {
      createEvent({
        name: '[Lists] Create List',
        properties: { Feature: 'enrichment' },
      })

      createToast({ message: 'listAddingCompanies' })

      // No domains missing, go to list after import
      if (!form.domains.missing.length) {
        router.push(
          localePath({
            name: 'companies-lists-listId',
            params: { listId: `${form.listSelector.listId}` },
            query: { 'wait-companies': 'true' },
          }),
        )
      }
      // Domains missing, request enrichment job in addition to saving companies
      else {
        try {
          await actionsStore.requestAction({
            domains: form.domains.missing,
            job: 'enrich-companies',
            listId: form.listSelector.listId!,
            type: 'jobs:request',
          })

          router.push(
            localePath({
              name: 'companies-lists-listId',
              params: { listId: `${form.listSelector.listId}` },
              query: { 'wait-companies': 'true' },
            }),
          )
        }
        catch (error) {
          console.error(error)
        }
      }
    }
  }
  catch {
    createToast({
      message: 'somethingWentWrong',
      status: 'error',
    })
  }

  importing.value = false

  onClose()
}

function onBuyMoreCredits() {
  onClose()

  router.push(localePath({ name: 'settings-subscription' }))
}

await setForm()
</script>

<template>
  <UModal
    :ui="{ width: 'max-w-full sm:max-w-4xl' }"
    @close="onClose"
  >
    <UCard
      :ui="{
        body: {
          base: 'flex h-full flex-col gap-4 min-h-[70vh]',
        },
        header: {
          base: '!hidden',
        },
      }"
    >
      <UCard :ui="{ header: { base: 'bg-gray-100 dark:bg-gray-900 rounded-t-xl' }, body: { padding: '!p-0' }, footer: { base: '!hidden' } }">
        <template #header>
          <div class="flex items-center gap-2">
            <span>
              {{ t('quotes.columnsMatcher') }}
            </span>
          </div>
        </template>

        <div class="rounded-b-lg">
          <FieldColumnsMatcher
            v-model="form.columnsMatcher.columnsMatched"
            :columns="form.columnsMatcher.columns"
            size="sm"
          />
        </div>
      </UCard>

      <UCard
        v-if="hasNameMatchedColumn"
        :ui="{ header: { base: 'bg-gray-100 dark:bg-gray-900 rounded-t-xl text-balance' }, footer: { base: '!hidden' } }"
      >
        <template #header>
          <div class="flex items-center gap-2">
            <span>
              {{ t('quotes.countrySelector') }}
            </span>
          </div>
        </template>

        <FieldCountry
          v-model="form.countrySelector.country"
          show-all
        />
      </UCard>

      <div class="flex flex-col gap-4">
        <UCard :ui="{ header: { base: 'bg-gray-100 dark:bg-gray-900 rounded-t-xl' }, body: { padding: '!p-0' }, footer: { base: '!hidden' } }">
          <template #header>
            <div class="flex items-center gap-2">
              <span>
                {{ t('quotes.listSelector') }}
              </span>
            </div>
          </template>

          <div class="p-4">
            <ListSelector
              v-model="form.listSelector.listId"
              :add-to-list="false"
              feature="enrichment"
              :has-quote="false"
              :list-name="form.listSelector.listName"
            />
          </div>
        </UCard>

        <BaseQuote
          v-if="form.columnsMatcher.companiesCount > enrichmentStore.maxCompanies"
          size="sm"
          status="danger"
        >
          {{ t('quotes.overLimitDomains', { count: form.columnsMatcher.companiesCount, maxCompanies: enrichmentStore.maxCompanies }) }}
        </BaseQuote>
      </div>

      <UCard
        v-if="canImport === true"
        :ui="{ header: { base: '!hidden' }, footer: { base: '!hidden' } }"
      >
        <div class="flex flex-col gap-2">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div v-if="enricheableCompaniesCount.existingCompaniesCount > 0">
              <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ t('existingCompanies') }}
              </div>

              <div class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {{ numbro(enricheableCompaniesCount.existingCompaniesCount) }} {{ t('globals.company', enricheableCompaniesCount.existingCompaniesCount) }}
              </div>

              <div class="text-xs text-gray-600 dark:text-gray-400">
                {{ t('creditCost', { cost: costs.globals.company }) }}
              </div>
            </div>

            <div v-if="enricheableCompaniesCount.missingCompaniesCount > 0">
              <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ t('missingCompanies') }}
              </div>

              <div class="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {{ numbro(enricheableCompaniesCount.missingCompaniesCount) }}
              </div>

              <div class="text-xs text-gray-600 dark:text-gray-400">
                {{ t('creditCost', { cost: costs.actions['enrich-companies'] }) }}
              </div>
            </div>
          </div>

          <div class="border-t border-gray-200 pt-4 dark:border-gray-800">
            <div class="flex items-center justify-between">
              <div>
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ t('finalNumberOfCompanies') }}
                </div>

                <div class="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                  {{ numbro(enricheableCompaniesCount.enrichedCount) }} {{ t('globals.company', enricheableCompaniesCount.enrichedCount) }}
                </div>
              </div>

              <div class="flex flex-col">
                <div class="flex w-full justify-end text-lg font-bold">
                  <BaseCoin
                    hide-suffix
                    size="md"
                  >
                    <div class="flex flex-col items-end">
                      <div class="flex items-center gap-2">
                        <span
                          v-if="enricheableCompaniesCount.effectiveCost !== enricheableCompaniesCount.totalCost"
                          class="text-gray-600 line-through opacity-75 dark:text-gray-400"
                        >
                          {{ numbro(enricheableCompaniesCount.totalCost) }}
                        </span>

                        <span>
                          {{ numbro(enricheableCompaniesCount.effectiveCost) }}
                        </span>
                      </div>
                    </div>
                  </BaseCoin>
                </div>

                <span class="text-xs text-gray-600 dark:text-gray-400">
                  {{ numbro(enricheableCompaniesCount.creditsAfter) }} {{ t('creditsRemaining') }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="enricheableCompaniesCount.remainingTotal > 0"
            class="flex flex-col items-center justify-between gap-2 rounded-lg bg-red-50 p-2 md:flex-row dark:bg-red-900/20"
          >
            <div class="text-sm text-red-700 dark:text-red-300">
              <strong>{{ numbro(enricheableCompaniesCount.remainingTotal) }}</strong> {{ t('companiesNotEnriched', { count: numbro(enricheableCompaniesCount.remainingTotal) }) }}
            </div>

            <div class="flex w-full items-center gap-2 md:w-fit">
              <UButton
                class="hidden md:flex"
                :icon="getIcon('coins')"
                :label="t('buyMoreCredits')"
                :ui="{ icon: { base: 'text-indigo-400 dark:text-indigo-600' } }"
                @click="onBuyMoreCredits"
              />

              <UButton
                block
                class="md:hidden"
                :icon="getIcon('coins')"
                :label="t('buyMoreCredits')"
                :ui="{ icon: { base: 'text-indigo-400 dark:text-indigo-600' } }"
                @click="onBuyMoreCredits"
              />
            </div>
          </div>
        </div>
      </UCard>

      <template #footer>
        <div class="flex w-full items-center gap-4">
          <UTooltip
            class="w-full"
            :text="canImport === true ? '' : canImport"
          >
            <UButton
              v-if="enricheableCompaniesCount.enrichedCount > 0"
              block
              class="flex-1"
              :disabled="!(canImport === true)"
              :icon="getIcon('enrichmentFeature')"
              :label="t('enrichCompanies', { count: numbro(enricheableCompaniesCount.enrichedCount) })"
              size="xl"
              :ui="{ icon: { base: 'text-green-400 dark:text-green-600' } }"
              @click="onImportCompanies"
            />

            <UButton
              v-else
              block
              :icon="getIcon('coins')"
              :label="t('buyMoreCredits')"
              size="xl"
              :ui="{ icon: { base: 'text-indigo-400 dark:text-indigo-600' } }"
              @click="onBuyMoreCredits"
            />
          </UTooltip>
        </div>
      </template>
    </UCard>
  </UModal>
</template>

<i18n lang="json">
{
  "en": {
    "buyMoreCredits": "Buy more credits",
    "companiesNotEnriched": "company will not be enriched due to insufficient credits | companies will not be enriched due to insufficient credits",
    "creditCost": "{cost} credit per company | {cost} credits per company",
    "creditsRemaining": "credits remaining",
    "enrichCompanies": "Import {count} companies",
    "existingCompanies": "Available in our database",
    "finalNumberOfCompanies": "Final number of companies to be added to your list",
    "missingCompanies": "New domains we must analyze",
    "mustMatchColumns": "You must match one column first.",
    "mustSelectList": "You must select a list first.",
    "quotes": {
      "columnsMatcher": "By associating your file's columns with our schema, you help us better identify companies from your file.",
      "countrySelector": "Are all the companies you're importing based in the same country? Providing this information will help our algorithms better identify them using the Company Name field.",
      "listSelector": "Should we add the companies to a new list or an existing one?",
      "overLimitDomains": "The file you submitted contains {count} domains, but there is a limit of {maxCompanies} domains per import."
    },
    "totalCost": "Total cost"
  },
  "es": {
    "buyMoreCredits": "Comprar más créditos",
    "companiesNotEnriched": "empresa no será enriquecida debido a créditos insuficientes | empresas no serán enriquecidas debido a créditos insuficientes",
    "creditCost": "{cost} crédito por empresa | {cost} créditos por empresa",
    "creditsRemaining": "créditos restantes",
    "enrichCompanies": "Importar {count} empresas",
    "existingCompanies": "Disponibles en nuestra base de datos",
    "finalNumberOfCompanies": "Número final de empresas a agregar a tu lista",
    "missingCompanies": "Dominios nuevos que debemos analizar",
    "mustMatchColumns": "Debes asociar al menos una columna.",
    "mustSelectList": "Debes seleccionar primero una lista.",
    "quotes": {
      "columnsMatcher": "Al asociar las columnas de tu archivo con nuestro esquema, nos ayudas a identificar mejor las empresas de tu archivo.",
      "countrySelector": "¿Todas las empresas que estás importando están basadas en el mismo país? Proporcionar esta información ayudará a nuestros algoritmos a identificarlas mejor usando el campo Nombre de la Empresa.",
      "listSelector": "¿Deberíamos añadir las empresas a una nueva lista o a una existente?",
      "overLimitDomains": "El archivo que has enviado contiene {count} dominios, pero hay un límite de {maxCompanies} dominios por importación."
    },
    "totalCost": "Costo total"
  },
  "fr": {
    "buyMoreCredits": "Acheter plus de crédits",
    "companiesNotEnriched": "entreprise ne sera pas enrichie en raison de crédits insuffisants | entreprises ne seront pas enrichies en raison de crédits insuffisants",
    "creditCost": "{cost} crédit par entreprise | {cost} crédits par entreprise",
    "creditsRemaining": "crédits restants",
    "enrichCompanies": "Importer {count} entreprises",
    "existingCompanies": "Disponibles dans notre base de données",
    "finalNumberOfCompanies": "Nombre final d'entreprises à ajouter à votre liste",
    "missingCompanies": "Nouveaux domaines que nous devons analyser",
    "mustMatchColumns": "Vous devez d'abord associer une colonne.",
    "mustSelectList": "Vous devez d'abord sélectionner une liste.",
    "quotes": {
      "columnsMatcher": "En associant les colonnes de votre fichier avec notre schéma, vous nous aidez à mieux identifier les entreprises à partir de votre fichier.",
      "countrySelector": "Est-ce que toutes les entreprises que vous importez sont basées dans le même pays ? Fournir cette information aidera nos algorithmes à mieux les identifier en utilisant le champ \"Nom de l'entreprise\".",
      "listSelector": "Devons-nous ajouter les entreprises à une nouvelle liste ou à une existante ?",
      "overLimitDomains": "Le fichier que vous avez soumis contient {count} domaines, mais il y a une limite de {maxCompanies} domaines par import."
    },
    "totalCost": "Coût total"
  }
}
</i18n>
